'use client';

import React, { useState } from 'react';
import { Link2, Unlink, ExternalLink, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useTradingData } from '@/hooks/useTradingData';
import LoadingSpinner from '@/components/LoadingSpinner';

const DERIV_TOKEN_URL = 'https://app.deriv.com/account/api-token';

export const DerivConnect: React.FC = () => {
  const { derivAccount, connectDeriv, refreshData } = useTradingData();
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isConnected = !!derivAccount?.loginid;

  const handleConnect = async () => {
    if (!token.trim()) {
      setError('Paste your Deriv API token first.');
      return;
    }
    setConnecting(true);
    setError(null);
    setSuccess(false);
    try {
      const ok = await connectDeriv(token.trim());
      if (ok) {
        setSuccess(true);
        setToken('');
        await refreshData();
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('Could not validate that token. Double-check it and make sure it has "Read", "Trade" and "Trading information" scopes enabled.');
      }
    } catch (err: any) {
      setError(err?.message || 'Connection failed. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="bg-surface-card rounded-xl border border-surface-border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-300">Deriv Account</h3>
        {isConnected ? (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-trade-green bg-trade-green/10 px-2.5 py-1 rounded-full">
            <CheckCircle2 className="w-3.5 h-3.5" /> Connected
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-trade-red bg-trade-red/10 px-2.5 py-1 rounded-full">
            <Unlink className="w-3.5 h-3.5" /> Not Connected
          </span>
        )}
      </div>

      {isConnected ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Login ID</p>
              <p className="text-white font-mono">{derivAccount.loginid}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Account type</p>
              <p className="text-white">{derivAccount.is_virtual ? 'Demo' : 'Real'}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            To switch accounts (e.g. connect your real account), paste a new API token below.
          </p>
          <TokenInput
            token={token} setToken={setToken} showToken={showToken} setShowToken={setShowToken}
          />
          {error && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-trade-red/10 text-trade-red text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-trade-green/10 text-trade-green border border-trade-green/30 hover:bg-trade-green/20 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {connecting ? <LoadingSpinner size="sm" /> : <Link2 className="w-4 h-4" />}
            {connecting ? 'Connecting...' : 'Reconnect / Switch Account'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-400 leading-relaxed">
            Connect your Deriv account to see your real balance and let the bot trade. Grab an API token from your
            Deriv account settings — enable at least <span className="text-gray-300 font-medium">Read</span>,{' '}
            <span className="text-gray-300 font-medium">Trade</span> and{' '}
            <span className="text-gray-300 font-medium">Trading information</span> scopes.
          </p>
          <a
            href={DERIV_TOKEN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-trade-green hover:underline"
          >
            Get your API token on Deriv <ExternalLink className="w-3 h-3" />
          </a>
          <TokenInput
            token={token} setToken={setToken} showToken={showToken} setShowToken={setShowToken}
          />
          {error && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-trade-red/10 text-trade-red text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-trade-green/10 text-trade-green text-xs">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Connected successfully!
            </div>
          )}
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-trade-green/10 text-trade-green border border-trade-green/30 hover:bg-trade-green/20 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {connecting ? <LoadingSpinner size="sm" /> : <Link2 className="w-4 h-4" />}
            {connecting ? 'Connecting...' : 'Connect Deriv Account'}
          </button>
          <p className="text-[11px] text-gray-500">
            Your token is validated server-side and never stored — only your login ID and balance are saved.
          </p>
        </div>
      )}
    </div>
  );
};

function TokenInput({ token, setToken, showToken, setShowToken }: {
  token: string; setToken: (v: string) => void; showToken: boolean; setShowToken: (v: boolean) => void;
}) {
  return (
    <div className="relative">
      <input
        type={showToken ? 'text' : 'password'}
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Paste your Deriv API token"
        className="w-full bg-surface-hover text-white rounded-lg px-3 py-2.5 pr-10 text-sm border border-surface-border focus:border-trade-green focus:outline-none font-mono"
      />
      <button
        type="button"
        onClick={() => setShowToken(!showToken)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
      >
        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default DerivConnect;
